import { useCallback, useEffect, useRef, useState } from "react";

interface AutospinOptions {
    canSpin: boolean;
    isBusy: boolean;
    spin: () => Promise<boolean>;
    nextSpinDelay?: () => number;
}

/** Schedule only after the previous authoritative result and its animation finish. */
export function useAutospin(options: AutospinOptions) {
    const [isAutoSpinning, setIsAutoSpinning] = useState(false);
    const [completion, setCompletion] = useState(0);
    const activeRef = useRef(false);
    const startAllowedRef = useRef(true);
    const pendingRef = useRef(false);
    const mountedRef = useRef(false);
    const optionsRef = useRef(options);
    optionsRef.current = options;

    const stop = useCallback(() => {
        activeRef.current = false;
        startAllowedRef.current = false;
        setIsAutoSpinning(false);
    }, []);

    const start = useCallback(() => {
        if (
            !startAllowedRef.current ||
            !optionsRef.current.canSpin ||
            document.querySelector("dialog[open]")
        )
            return;
        activeRef.current = true;
        setIsAutoSpinning(true);
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        // All app sheets use native dialogs, including nested friend/settings flows.
        const observer = new MutationObserver(() => {
            if (document.querySelector("dialog[open]")) stop();
        });
        observer.observe(document.body, {
            subtree: true,
            attributes: true,
            attributeFilter: ["open"],
            childList: true,
        });
        return () => {
            mountedRef.current = false;
            activeRef.current = false;
            observer.disconnect();
        };
    }, [stop]);

    const spinOnce = useCallback(async () => {
        if (pendingRef.current || optionsRef.current.isBusy) return false;
        startAllowedRef.current = true;
        pendingRef.current = true;
        try {
            const success = await optionsRef.current.spin();
            if (!success && mountedRef.current) stop();
            return success;
        } catch (error) {
            console.error("[autospin] spin failed", error);
            if (mountedRef.current) stop();
            return false;
        } finally {
            pendingRef.current = false;
            if (mountedRef.current) setCompletion((value) => value + 1);
        }
    }, [stop]);

    useEffect(() => {
        if (!isAutoSpinning) return;
        // A spin may consume the last energy while its animation is still running.
        if (!options.canSpin) {
            stop();
            return;
        }
        if (options.isBusy || pendingRef.current) return;
        const timer = window.setTimeout(async () => {
            const current = optionsRef.current;
            if (!activeRef.current || pendingRef.current || current.isBusy)
                return;
            if (!current.canSpin || document.querySelector("dialog[open]")) {
                stop();
                return;
            }
            await spinOnce();
        }, optionsRef.current.nextSpinDelay?.() ?? 1000);
        return () => window.clearTimeout(timer);
    }, [
        completion,
        isAutoSpinning,
        options.canSpin,
        options.isBusy,
        spinOnce,
        stop,
    ]);

    return { isAutoSpinning, start, stop, spin: spinOnce };
}
